import { addBlankSlide } from "./slideInsert";
import { CHART_PALETTE, SLIDE_CANVAS } from "./slideModel";
import type {
  ChartElement,
  ShapeKind,
  SlideElement,
  SlideModel,
  TableElement,
  TextAlign,
  TextElement,
} from "./slideModel";

/**
 * Renders a structured SlideModel into a new PowerPoint slide via Office.js, so
 * the inserted deck matches the in-app preview (which draws the same model).
 *
 * The model canvas is 960×540 — identical to a 16:9 PowerPoint slide in points
 * — so element coordinates map 1:1 (no scaling). Text and shapes render
 * faithfully; images, charts, and tables are approximated because the
 * PowerPoint JS API has no native image/chart/table insertion on a slide.
 */

type Ppt = typeof PowerPoint;

function ppt(): Ppt {
  const g = globalThis as { PowerPoint?: Ppt };
  if (!g.PowerPoint) throw new Error("PowerPoint API unavailable — not running inside PowerPoint.");
  return g.PowerPoint;
}

function geometry(kind: ShapeKind): PowerPoint.GeometricShapeType {
  const T = PowerPoint.GeometricShapeType;
  const map: Record<ShapeKind, PowerPoint.GeometricShapeType> = {
    rectangle: T.rectangle,
    roundedRectangle: T.roundRectangle,
    ellipse: T.ellipse,
    diamond: T.diamond,
    triangle: T.triangle,
    rightArrow: T.rightArrow,
    chevron: T.chevron,
    pentagon: T.pentagon,
    star: T.star5,
  };
  return map[kind] ?? T.rectangle;
}

function hAlign(a?: TextAlign): PowerPoint.ParagraphHorizontalAlignment {
  const A = PowerPoint.ParagraphHorizontalAlignment;
  if (a === "center") return A.center;
  if (a === "right") return A.right;
  if (a === "justify") return A.justify;
  return A.left;
}

/** Style a shape/textbox's text run + paragraph. */
function styleText(
  shape: PowerPoint.Shape,
  opts: { fontSize?: number; fontColor?: string; bold?: boolean; italic?: boolean; align?: TextAlign },
): void {
  const range = shape.textFrame.textRange;
  const font = range.font;
  if (opts.fontSize) font.size = opts.fontSize;
  if (opts.fontColor) font.color = opts.fontColor;
  if (opts.bold !== undefined) font.bold = opts.bold;
  if (opts.italic !== undefined) font.italic = opts.italic;
  range.paragraphFormat.horizontalAlignment = hAlign(opts.align);
}

function addTextEl(slide: PowerPoint.Slide, el: TextElement): void {
  const text = el.text ?? (el.bullets ?? []).map((b) => `• ${b}`).join("\n");
  const box = slide.shapes.addTextBox(text, {
    left: el.left,
    top: el.top,
    width: el.width,
    height: el.height,
  });
  if (el.fill) box.fill.setSolidColor(el.fill);
  styleText(box, { fontSize: el.fontSize, fontColor: el.fontColor, bold: el.bold, italic: el.italic, align: el.align });
}

function addShapeEl(
  slide: PowerPoint.Slide,
  el: Extract<SlideElement, { type: "shape" }>,
): void {
  const shape = slide.shapes.addGeometricShape(geometry(el.shape), {
    left: el.left,
    top: el.top,
    width: el.width,
    height: el.height,
  });
  if (el.fill) shape.fill.setSolidColor(el.fill);
  if (el.borderColor) {
    shape.lineFormat.color = el.borderColor;
    shape.lineFormat.weight = el.borderWidth ?? 1;
  }
  if (el.text) {
    shape.textFrame.textRange.text = el.text;
    styleText(shape, { fontSize: el.fontSize, fontColor: el.fontColor ?? "#FFFFFF", align: "center" });
  }
}

/** Images aren't insertable on an arbitrary slide via the API — approximate with a filled block + label. */
function addImageApprox(
  slide: PowerPoint.Slide,
  el: Extract<SlideElement, { type: "image" }>,
): void {
  const block = slide.shapes.addGeometricShape(PowerPoint.GeometricShapeType.rectangle, {
    left: el.left,
    top: el.top,
    width: el.width,
    height: el.height,
  });
  block.fill.setSolidColor("#1A9E8F");
  block.textFrame.textRange.text = el.alt || "Image";
  styleText(block, { fontSize: 14, fontColor: "#FFFFFF", align: "center" });
}

/** Approximate a column/bar chart as filled rectangles (no native chart API). */
function addChartApprox(slide: PowerPoint.Slide, el: ChartElement): void {
  const data = el.series[0]?.data ?? [];
  // Treat an explicitly-empty `colors: []` as "use defaults" — otherwise
  // colors[i % 0] is colors[NaN] → undefined → setSolidColor(undefined) throws.
  const colors = el.colors?.length ? el.colors : CHART_PALETTE;
  const pad = 8;
  const baseY = el.top + el.height - pad;
  const plotH = el.height - pad * 2;
  const max = Math.max(1, ...data);
  const n = Math.max(1, data.length);
  const bw = ((el.width - pad * 2) / n) * 0.6;
  const gap = ((el.width - pad * 2) / n) * 0.4;
  data.forEach((v, i) => {
    const h = (v / max) * plotH;
    const bar = slide.shapes.addGeometricShape(PowerPoint.GeometricShapeType.rectangle, {
      left: el.left + pad + i * (bw + gap) + gap / 2,
      top: baseY - h,
      width: bw,
      height: h,
    });
    bar.fill.setSolidColor(colors[i % colors.length]!);
  });
}

/** Approximate a table as stacked row text boxes (no native table API). */
function addTableApprox(slide: PowerPoint.Slide, el: TableElement): void {
  const rows = el.rows;
  if (rows.length === 0) return;
  const rowH = el.height / rows.length;
  rows.forEach((row, r) => {
    const box = slide.shapes.addTextBox(row.join("     "), {
      left: el.left,
      top: el.top + r * rowH,
      width: el.width,
      height: rowH,
    });
    const isHeader = el.header && r === 0;
    if (isHeader) box.fill.setSolidColor("#0B5DAA");
    styleText(box, { fontSize: 12, fontColor: isHeader ? "#FFFFFF" : "#1A1A1A", bold: isHeader });
  });
}

function renderElement(slide: PowerPoint.Slide, el: SlideElement): void {
  switch (el.type) {
    case "text":
      addTextEl(slide, el);
      break;
    case "shape":
      addShapeEl(slide, el);
      break;
    case "image":
      addImageApprox(slide, el);
      break;
    case "chart":
      addChartApprox(slide, el);
      break;
    case "table":
      addTableApprox(slide, el);
      break;
    default:
      break; // "unsupported" → skip
  }
}

/** Adds one new slide and renders the model's elements onto it. Throws on host failure. */
export async function insertSlideFromModel(model: SlideModel): Promise<void> {
  await ppt().run(async (ctx) => {
    const slides = ctx.presentation.slides;
    await addBlankSlide(ctx, slides);

    const count = slides.getCount();
    await ctx.sync();
    const slide = slides.getItemAt(count.value - 1);

    // Full-slide background block (behind everything else).
    if (model.background) {
      const bg = slide.shapes.addGeometricShape(PowerPoint.GeometricShapeType.rectangle, {
        left: 0,
        top: 0,
        width: SLIDE_CANVAS.width,
        height: SLIDE_CANVAS.height,
      });
      bg.fill.setSolidColor(model.background);
      bg.lineFormat.visible = false;
    }

    for (const el of model.elements) renderElement(slide, el);
    await ctx.sync();
  });
}
